import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { useAppState } from "../context/AppStateContext";
import { useToast } from "../context/ToastContext";
import {
  confirmIssueMaterialDispatchOtp,
  fetchIssueMaterialPendingOutward,
  fetchStoreOutwardRegister,
  sendIssueMaterialDispatchOtp,
  type IssueMaterialDispatchOtpPlan,
  type IssueMaterialRow,
  type StoreOutwardRow,
} from "../api/mrrInventoryApi";
import { findSubcontractorMaster, isSubcontractorLinkageComplete } from "../utils/subcontractor";
import { parseQuantityInput, sanitizeQtyFieldInput } from "../utils/qtyInput";
import { applicationMatchesStoreYard } from "../utils/storeYards";

function formatOutwardWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function InventoryOutwardPage() {
  const { applications, subcontractorMasters, storeYards, geoZones } = useAppState();
  const { showToast } = useToast();
  const sortedYards = useMemo(() => [...storeYards].sort((a, b) => a.sortOrder - b.sortOrder), [storeYards]);
  const sortedMasters = useMemo(
    () => [...subcontractorMasters].sort((a, b) => a.firmName.localeCompare(b.firmName)),
    [subcontractorMasters],
  );

  const [outwardViewMode, setOutwardViewMode] = useState<"dispatch" | "register">("dispatch");
  const [issueStoreYardId, setIssueStoreYardId] = useState("");
  const [subcontractorMasterId, setSubcontractorMasterId] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(() => new Set());
  const [dispatchMode, setDispatchMode] = useState<"full" | "partial">("full");
  const [qtyInput, setQtyInput] = useState("");
  const [outwardBeneficiarySearch, setOutwardBeneficiarySearch] = useState("");
  const [outwardDistrictFilter, setOutwardDistrictFilter] = useState<string>("all");
  const [outwardMandalFilter, setOutwardMandalFilter] = useState<string>("all");
  const [outwardVillageFilter, setOutwardVillageFilter] = useState<string>("all");
  const [pendingRows, setPendingRows] = useState<IssueMaterialRow[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpConfirming, setOtpConfirming] = useState(false);
  const [otpChallengeId, setOtpChallengeId] = useState<string | null>(null);
  const [otpEmailHint, setOtpEmailHint] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpInlineError, setOtpInlineError] = useState<string | null>(null);
  const otpPayloadRef = useRef<{
    subcontractorMasterId: string;
    storeYardId: string;
    storeYardName?: string;
    plans: IssueMaterialDispatchOtpPlan[];
  } | null>(null);

  const [registerRows, setRegisterRows] = useState<StoreOutwardRow[]>([]);
  const [registerLoading, setRegisterLoading] = useState(true);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [listStoreYardId, setListStoreYardId] = useState("");
  const [listSubcontractorId, setListSubcontractorId] = useState("");
  const [listSearch, setListSearch] = useState("");
  const [listSort, setListSort] = useState<"asc" | "desc">("desc");

  const allEligibleBeneficiaries = useMemo(() => {
    const sid = subcontractorMasterId.trim();
    if (!sid) return [];
    return applications
      .filter((a) => {
        const m = findSubcontractorMaster(subcontractorMasters, a.subcontractor.subcontractorMasterId);
        return a.subcontractor.subcontractorMasterId === sid && isSubcontractorLinkageComplete(a.subcontractor, m);
      })
      .sort((a, b) => a.mobile.beneficiaryName.localeCompare(b.mobile.beneficiaryName));
  }, [applications, subcontractorMasters, subcontractorMasterId]);

  const beneficiaryOptions = useMemo(() => {
    if (!subcontractorMasterId.trim()) return [];
    const presentAppIds = new Set(pendingRows.map((r) => r.applicationId));
    return allEligibleBeneficiaries.filter((a) => presentAppIds.has(a.id));
  }, [allEligibleBeneficiaries, pendingRows, subcontractorMasterId]);

  const eligibleAppMap = useMemo(() => {
    const m = new Map<string, (typeof allEligibleBeneficiaries)[number]>();
    for (const a of allEligibleBeneficiaries) m.set(a.id, a);
    return m;
  }, [allEligibleBeneficiaries]);

  const issueRowsForSelection = useMemo(() => {
    const sid = subcontractorMasterId.trim();
    if (!sid) return [];
    const selectedStore = issueStoreYardId ? storeYards.find((y) => y.id === issueStoreYardId) : null;
    return pendingRows.filter((row) => {
      if (row.subcontractorMasterId !== sid || row.status === "completed" || row.balanceQty <= 0) return false;
      // If a scoped store yard is selected, only show rows for beneficiaries in its scope
      if (selectedStore) {
        const app = eligibleAppMap.get(row.applicationId);
        if (app && !applicationMatchesStoreYard(app, selectedStore, geoZones)) return false;
      }
      return true;
    });
  }, [pendingRows, subcontractorMasterId, issueStoreYardId, storeYards, eligibleAppMap, geoZones]);

  /** Pending rows after geo + search filters (each row carries its own beneficiary). */
  const filteredIssueRows = useMemo(() => {
    const q = outwardBeneficiarySearch.trim().toLowerCase();
    return issueRowsForSelection.filter((row) => {
      const app = eligibleAppMap.get(row.applicationId);
      if (!app) return false;
      if (outwardDistrictFilter !== "all" && app.district !== outwardDistrictFilter) return false;
      if (outwardMandalFilter !== "all" && app.mandal !== outwardMandalFilter) return false;
      if (outwardVillageFilter !== "all" && app.village !== outwardVillageFilter) return false;
      if (!q) return true;
      const hay = [
        row.beneficiaryName,
        row.beneficiaryMobileApplicationId,
        row.applicationId,
        row.resourceItemLabel,
        row.catalogStandardItemId,
        row.milestoneTitle,
        app.district,
        app.mandal,
        app.village,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    eligibleAppMap,
    issueRowsForSelection,
    outwardBeneficiarySearch,
    outwardDistrictFilter,
    outwardMandalFilter,
    outwardVillageFilter,
  ]);

  /** Group filtered rows by milestone + item + balanceQty so beneficiaries with the same item & qty cluster together. */
  type MaterialGroup = {
    key: string;
    milestoneTemplateId: string;
    milestoneTitle: string;
    standardResourceTitle: string;
    resourceItemLabel: string;
    resourceItemUnit: string;
    catalogStandardItemId: string;
    balanceQty: number;
    rows: IssueMaterialRow[];
  };
  const materialGroups = useMemo<MaterialGroup[]>(() => {
    const map = new Map<string, MaterialGroup>();
    for (const r of filteredIssueRows) {
      const key = `${r.milestoneTemplateId}|${r.standardResourceItemId}|${r.balanceQty}|${r.resourceItemUnit}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          milestoneTemplateId: r.milestoneTemplateId,
          milestoneTitle: r.milestoneTitle,
          standardResourceTitle: r.standardResourceTitle,
          resourceItemLabel: r.resourceItemLabel,
          resourceItemUnit: r.resourceItemUnit,
          catalogStandardItemId: r.catalogStandardItemId,
          balanceQty: r.balanceQty,
          rows: [],
        };
        map.set(key, g);
      }
      g.rows.push(r);
    }
    const out = [...map.values()];
    out.forEach((g) =>
      g.rows.sort((a, b) =>
        (a.beneficiaryName ?? "").localeCompare(b.beneficiaryName ?? ""),
      ),
    );
    out.sort(
      (a, b) =>
        a.milestoneTitle.localeCompare(b.milestoneTitle) ||
        a.resourceItemLabel.localeCompare(b.resourceItemLabel) ||
        b.balanceQty - a.balanceQty,
    );
    return out;
  }, [filteredIssueRows]);

  const selectedRows = useMemo(
    () => filteredIssueRows.filter((r) => selectedRowIds.has(r.id)),
    [filteredIssueRows, selectedRowIds],
  );

  const toggleOneRow = useCallback((id: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const groupSelectionState = useCallback(
    (g: MaterialGroup): true | false | "indeterminate" => {
      let on = 0;
      for (const r of g.rows) if (selectedRowIds.has(r.id)) on += 1;
      if (on === 0) return false;
      if (on === g.rows.length) return true;
      return "indeterminate";
    },
    [selectedRowIds],
  );

  const toggleWholeGroup = useCallback(
    (g: MaterialGroup) => {
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        const allOn = g.rows.every((r) => next.has(r.id));
        if (allOn) g.rows.forEach((r) => next.delete(r.id));
        else g.rows.forEach((r) => next.add(r.id));
        return next;
      });
    },
    [],
  );

  const outwardDistrictOptions = useMemo(() => {
    const list = [...new Set(beneficiaryOptions.map((a) => String(a.district ?? "").trim()).filter(Boolean))];
    list.sort((a, b) => a.localeCompare(b));
    return list;
  }, [beneficiaryOptions]);

  const outwardMandalOptions = useMemo(() => {
    const list = beneficiaryOptions
      .filter((a) => outwardDistrictFilter === "all" || a.district === outwardDistrictFilter)
      .map((a) => String(a.mandal ?? "").trim())
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [beneficiaryOptions, outwardDistrictFilter]);

  const outwardVillageOptions = useMemo(() => {
    const list = beneficiaryOptions
      .filter((a) => outwardDistrictFilter === "all" || a.district === outwardDistrictFilter)
      .filter((a) => outwardMandalFilter === "all" || a.mandal === outwardMandalFilter)
      .map((a) => String(a.village ?? "").trim())
      .filter(Boolean);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b));
  }, [beneficiaryOptions, outwardDistrictFilter, outwardMandalFilter]);

  const filteredOutwardBeneficiaries = useMemo(() => {
    const q = outwardBeneficiarySearch.trim().toLowerCase();
    return beneficiaryOptions.filter((a) => {
      if (outwardDistrictFilter !== "all" && a.district !== outwardDistrictFilter) return false;
      if (outwardMandalFilter !== "all" && a.mandal !== outwardMandalFilter) return false;
      if (outwardVillageFilter !== "all" && a.village !== outwardVillageFilter) return false;
      if (!q) return true;
      const hay = [
        a.mobile.beneficiaryName,
        a.mobile.applicationId,
        a.district,
        a.mandal,
        a.village,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    beneficiaryOptions,
    outwardBeneficiarySearch,
    outwardDistrictFilter,
    outwardMandalFilter,
    outwardVillageFilter,
  ]);

  const outwardStoreYardSelectOptions = useMemo(
    () => [
      { value: "", label: "Select…" },
      ...sortedYards.map((y) => ({ value: y.id, label: y.name })),
    ],
    [sortedYards],
  );

  const outwardSubcontractorSelectOptions = useMemo(
    () => [
      { value: "", label: "Select…" },
      ...sortedMasters.map((m) => ({ value: m.id, label: m.firmName.trim() || m.id })),
    ],
    [sortedMasters],
  );

  const outwardDistrictSelectOptions = useMemo(
    () => [
      { value: "all", label: "All districts" },
      ...outwardDistrictOptions.map((d) => ({ value: d, label: d })),
    ],
    [outwardDistrictOptions],
  );
  const outwardMandalSelectOptions = useMemo(
    () => [
      { value: "all", label: "All mandals" },
      ...outwardMandalOptions.map((m) => ({ value: m, label: m })),
    ],
    [outwardMandalOptions],
  );
  const outwardVillageSelectOptions = useMemo(
    () => [
      { value: "all", label: "All villages" },
      ...outwardVillageOptions.map((v) => ({ value: v, label: v })),
    ],
    [outwardVillageOptions],
  );

  const outwardDispatchModeOptions = useMemo(
    () => [
      { value: "full", label: "Full balance" },
      { value: "partial", label: "Partial qty (single row)" },
    ],
    [],
  );

  /** Partial dispatch is only meaningful when a single row is selected — otherwise force "full". */
  const partialAllowed = selectedRows.length === 1;


  useEffect(() => {
    let cancelled = false;
    setPendingLoading(true);
    void fetchIssueMaterialPendingOutward({
      limit: 2000,
      subcontractorMasterId: subcontractorMasterId.trim() || undefined,
    })
      .then((list) => {
        if (!cancelled) setPendingRows(list);
      })
      .catch(() => {
        if (!cancelled) setPendingRows([]);
      })
      .finally(() => {
        if (!cancelled) setPendingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [subcontractorMasterId]);

  const loadRegister = useCallback(async () => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      const list = await fetchStoreOutwardRegister({
        limit: 800,
        storeYardId: listStoreYardId.trim() || undefined,
        subcontractorMasterId: listSubcontractorId.trim() || undefined,
        search: listSearch.trim() || undefined,
        sort: listSort,
      });
      setRegisterRows(list);
    } catch (e) {
      setRegisterError(e instanceof Error ? e.message : String(e));
      setRegisterRows([]);
    } finally {
      setRegisterLoading(false);
    }
  }, [listStoreYardId, listSubcontractorId, listSearch, listSort]);

  useEffect(() => {
    void loadRegister();
  }, [loadRegister]);

  useEffect(() => {
    if (!issueStoreYardId && sortedYards[0]?.id) {
      setIssueStoreYardId(sortedYards[0].id);
    }
  }, [issueStoreYardId, sortedYards]);

  useEffect(() => {
    setSelectedRowIds(new Set());
    setOutwardBeneficiarySearch("");
    setOutwardDistrictFilter("all");
    setOutwardMandalFilter("all");
    setOutwardVillageFilter("all");
  }, [subcontractorMasterId]);

  useEffect(() => {
    setOutwardMandalFilter("all");
    setOutwardVillageFilter("all");
  }, [outwardDistrictFilter]);

  useEffect(() => {
    setOutwardVillageFilter("all");
  }, [outwardMandalFilter]);

  /** Drop selected ids that no longer match filters or no longer have a balance. */
  useEffect(() => {
    const valid = new Set(filteredIssueRows.map((r) => r.id));
    setSelectedRowIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (valid.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filteredIssueRows]);

  useEffect(() => {
    setDispatchMode("full");
    setQtyInput("");
  }, [issueStoreYardId, subcontractorMasterId]);

  useEffect(() => {
    if (dispatchMode === "full") setQtyInput("");
  }, [dispatchMode]);

  /** Force "full" mode whenever 0 or 2+ rows are selected — partial only meaningful for one row. */
  useEffect(() => {
    if (!partialAllowed && dispatchMode === "partial") setDispatchMode("full");
  }, [partialAllowed, dispatchMode]);

  const sendOtpChallenge = useCallback(async () => {
    const payload = otpPayloadRef.current;
    if (!payload) {
      setOtpInlineError("Nothing to send. Close this dialog and start again.");
      return;
    }
    setOtpSending(true);
    setOtpInlineError(null);
    setOtpChallengeId(null);
    setOtpEmailHint("");
    setOtpInput("");
    try {
      const r = await sendIssueMaterialDispatchOtp(payload);
      setOtpChallengeId(r.challengeId);
      setOtpEmailHint(r.emailHint);
    } catch (e) {
      setOtpInlineError(e instanceof Error ? e.message : String(e));
    } finally {
      setOtpSending(false);
    }
  }, []);

  const closeOtpModal = useCallback(() => {
    if (otpConfirming) return;
    setOtpModalOpen(false);
    otpPayloadRef.current = null;
    setOtpChallengeId(null);
    setOtpEmailHint("");
    setOtpInput("");
    setOtpInlineError(null);
    setOtpSending(false);
  }, [otpConfirming]);

  const validateOutwardDispatchPayload = ():
    | { error: string }
    | {
        ok: true;
        payload: {
          subcontractorMasterId: string;
          storeYardId: string;
          storeYardName?: string;
          plans: IssueMaterialDispatchOtpPlan[];
        };
      } => {
    if (!issueStoreYardId.trim()) {
      return { error: "Select a store / yard." };
    }
    if (!subcontractorMasterId.trim()) {
      return { error: "Select the subcontractor receiving stock for the beneficiary." };
    }
    if (selectedRows.length === 0) {
      return { error: "Select at least one beneficiary row to dispatch." };
    }
    const yard = sortedYards.find((y) => y.id === issueStoreYardId.trim());
    const sid = subcontractorMasterId.trim();

    let partialQty = 0;
    if (dispatchMode === "partial") {
      if (selectedRows.length !== 1) {
        return { error: "Partial qty is only allowed when exactly one row is selected." };
      }
      partialQty = parseQuantityInput(qtyInput);
      if (!(partialQty > 0)) {
        return { error: "Enter a partial quantity greater than zero." };
      }
      const onlyRow = selectedRows[0]!;
      if (partialQty > onlyRow.balanceQty) {
        return { error: `Quantity cannot exceed balance (${onlyRow.balanceQty}).` };
      }
    }

    for (const r of selectedRows) {
      if (r.status === "completed" || r.balanceQty <= 0) {
        return {
          error: `Material issue for "${r.resourceItemLabel}" (${r.beneficiaryName ?? r.applicationId}) is already completed.`,
        };
      }
      const app = applications.find((a) => a.id === r.applicationId);
      if (!app) {
        return { error: `Beneficiary not found for row ${r.id}.` };
      }
      if (app.subcontractor.subcontractorMasterId !== sid) {
        return {
          error: `Beneficiary "${r.beneficiaryName ?? r.applicationId}" is not linked to this subcontractor.`,
        };
      }
    }

    const plans: IssueMaterialDispatchOtpPlan[] = selectedRows.map((r) =>
      dispatchMode === "partial"
        ? { issueId: r.id, mode: "partial", qty: partialQty }
        : { issueId: r.id, mode: "full" },
    );

    return {
      ok: true,
      payload: {
        subcontractorMasterId: sid,
        storeYardId: issueStoreYardId.trim(),
        storeYardName: yard?.name,
        plans,
      },
    };
  };

  const beginOutwardOtpFlow = async () => {
    const v = validateOutwardDispatchPayload();
    if ("error" in v) {
      showToast(v.error, "error");
      return;
    }
    otpPayloadRef.current = v.payload;
    setOtpModalOpen(true);
    setOtpChallengeId(null);
    setOtpEmailHint("");
    setOtpInput("");
    setOtpInlineError(null);
    await sendOtpChallenge();
  };

  const confirmOtpAndDispatch = async () => {
    const digits = otpInput.replace(/\D/g, "").trim();
    if (digits.length !== 6) {
      setOtpInlineError("Enter the 6-digit OTP sent to the subcontractor’s email.");
      return;
    }
    if (!otpChallengeId) {
      setOtpInlineError("No active OTP. Use “Resend code” or close and try again.");
      return;
    }
    const sid = otpPayloadRef.current?.subcontractorMasterId ?? subcontractorMasterId.trim();
    setOtpConfirming(true);
    setOtpInlineError(null);
    setSubmitting(true);
    try {
      const res = await confirmIssueMaterialDispatchOtp({ challengeId: otpChallengeId, otp: digits });
      showToast(`Outward dispatch recorded for ${res.dispatched} row(s).`, "success");
      setOtpModalOpen(false);
      otpPayloadRef.current = null;
      setOtpChallengeId(null);
      setOtpEmailHint("");
      setOtpInput("");
      setSelectedRowIds(new Set());
      setQtyInput("");
      setDispatchMode("full");
      await loadRegister();
      const refreshedPending = await fetchIssueMaterialPendingOutward({
        limit: 2000,
        subcontractorMasterId: sid || undefined,
      });
      setPendingRows(refreshedPending);
    } catch (e) {
      setOtpInlineError(e instanceof Error ? e.message : String(e));
    } finally {
      setOtpConfirming(false);
      setSubmitting(false);
    }
  };

  const submitIssue = async () => {
    await beginOutwardOtpFlow();
  };

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: outwardViewMode === "register" ? "Outward Register" : "Outward" },
        ]}
        title={outwardViewMode === "register" ? "Inventory — Outward Register" : "Inventory — Outward"}
      />

      <div className="issue-mode-toggle-wrap">
        <div className="issue-mode-toggle" role="tablist" aria-label="Outward mode">
          <button
            type="button"
            role="tab"
            aria-selected={outwardViewMode === "dispatch"}
            className={`issue-mode-toggle__btn ${outwardViewMode === "dispatch" ? "is-active" : ""}`}
            onClick={() => setOutwardViewMode("dispatch")}
          >
            Outward
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={outwardViewMode === "register"}
            className={`issue-mode-toggle__btn ${outwardViewMode === "register" ? "is-active" : ""}`}
            onClick={() => setOutwardViewMode("register")}
          >
            Outward Register
          </button>
        </div>
      </div>

      {outwardViewMode === "dispatch" ? (
      <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
        <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
          Dispatch only from beneficiary-wise rows created in <strong>Issue Material Register</strong>. Choose store,
          subcontractor, beneficiary, then the pending issued line.{" "}
          <Link to="/inventory/issue-material">Issue material register</Link>
          {" · "}
          <Link to="/inventory/store-stocks">Store stocks</Link>
          {" · "}
          <Link to="/subcontractor-to-beneficiary">Subcontractor → beneficiary</Link>
        </p>

        <div className="inv-outward-issue">
          <h3 className="form-section-title">Dispatch issued material to site</h3>
          <p className="text-muted" style={{ marginTop: "-0.35rem", marginBottom: "0.75rem", fontSize: "0.88rem" }}>
            Step 1: Choose store, subcontractor and beneficiary {"->"} Step 2: Pick the pending issue row {"->"}
            Step 3: Select dispatch mode and quantity {"->"} Record dispatch.
          </p>
          <div className="inv-outward-issue__grid">
            <label className="inv-outward-issue__field inv-outward-issue__field--r1">
              <span className="inv-outward-issue__label">Step 1 — Store / yard</span>
              <SearchableSelect
                id="outward-store-yard"
                value={issueStoreYardId}
                onChange={setIssueStoreYardId}
                options={outwardStoreYardSelectOptions}
                placeholder="Select…"
              />
            </label>
            <label className="inv-outward-issue__field inv-outward-issue__field--r1">
              <span className="inv-outward-issue__label">Step 1 — Subcontractor</span>
              <SearchableSelect
                id="outward-subcontractor"
                value={subcontractorMasterId}
                onChange={setSubcontractorMasterId}
                options={outwardSubcontractorSelectOptions}
                placeholder="Select…"
              />
            </label>
            <div className="inv-outward-issue__field inv-outward-issue__field--full">
              <span className="inv-outward-issue__label">Step 1 — Beneficiary filters</span>
              <div className="goods-receipts-filters__grid" style={{ marginBottom: "0.5rem" }}>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Search</span>
                  <input
                    type="search"
                    className="form-control"
                    value={outwardBeneficiarySearch}
                    onChange={(e) => setOutwardBeneficiarySearch(e.target.value)}
                    placeholder="Beneficiary name / application id"
                    disabled={!subcontractorMasterId.trim()}
                  />
                </label>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">District</span>
                  <SearchableSelect
                    id="outward-beneficiary-district"
                    value={outwardDistrictFilter}
                    onChange={setOutwardDistrictFilter}
                    options={outwardDistrictSelectOptions}
                    placeholder="All districts"
                  />
                </label>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Mandal</span>
                  <SearchableSelect
                    id="outward-beneficiary-mandal"
                    value={outwardMandalFilter}
                    onChange={setOutwardMandalFilter}
                    options={outwardMandalSelectOptions}
                    placeholder="All mandals"
                  />
                </label>
                <label className="goods-receipts-filters__field">
                  <span className="goods-receipts-filters__label">Village</span>
                  <SearchableSelect
                    id="outward-beneficiary-village"
                    value={outwardVillageFilter}
                    onChange={setOutwardVillageFilter}
                    options={outwardVillageSelectOptions}
                    placeholder="All villages"
                  />
                </label>
              </div>
              <p className="text-muted" style={{ margin: 0, fontSize: "0.84rem" }}>
                Showing {filteredOutwardBeneficiaries.length} of {beneficiaryOptions.length} beneficiaries ·{" "}
                <strong>{selectedRows.length}</strong> row(s) selected
              </p>
            </div>
            <label className="inv-outward-issue__field inv-outward-issue__field--qty">
              <span className="inv-outward-issue__label">Step 3 — Dispatch mode</span>
              <SearchableSelect
                id="outward-dispatch-mode"
                value={dispatchMode}
                onChange={(v) => setDispatchMode(v === "partial" ? "partial" : "full")}
                options={outwardDispatchModeOptions}
                placeholder="Select mode…"
              />
            </label>
            <label className="inv-outward-issue__field inv-outward-issue__field--qty">
              <span className="inv-outward-issue__label">Step 3 — Quantity</span>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                value={qtyInput}
                onChange={(e) => setQtyInput(sanitizeQtyFieldInput(e.target.value))}
                placeholder={dispatchMode === "full" ? "Auto (full balance)" : "0"}
                aria-label="Quantity to dispatch"
                disabled={dispatchMode === "full"}
              />
            </label>
          </div>

          <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
            Step 2 — Pending issue rows (tick beneficiaries to dispatch)
          </h3>
          {!subcontractorMasterId.trim() ? (
            <p className="text-muted">Select a subcontractor above to see pending issue rows.</p>
          ) : pendingLoading ? (
            <p className="text-muted">Loading pending rows…</p>
          ) : materialGroups.length === 0 ? (
            <p className="text-muted">
              No pending issue rows match the current filters. Create rows in{" "}
              <Link to="/inventory/issue-material">Issue material register</Link>.
            </p>
          ) : (
            <div className="inv-outward-batch__table-wrap">
              <table className="data-table inv-outward-grouped-table">
                <thead>
                  <tr>
                    <th style={{ width: "2.5rem" }} aria-label="Select" />
                    <th>Beneficiary</th>
                    <th>Application ID</th>
                    <th>Milestone</th>
                    <th>Item</th>
                    <th>Catalog</th>
                    <th>Balance</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {materialGroups.map((g) => {
                    const st = groupSelectionState(g);
                    return (
                      <Fragment key={g.key}>
                        <tr className="inv-outward-grouped-table__group-header">
                          <td>
                            <input
                              type="checkbox"
                              checked={st === true}
                              ref={(el) => {
                                if (el) el.indeterminate = st === "indeterminate";
                              }}
                              onChange={() => toggleWholeGroup(g)}
                              aria-label={`Select all beneficiaries for ${g.resourceItemLabel}`}
                            />
                          </td>
                          <td colSpan={7}>
                            <strong>{g.resourceItemLabel}</strong>
                            <span className="text-muted"> ({g.standardResourceTitle})</span>
                            {" · "}
                            <strong>
                              {g.balanceQty} {g.resourceItemUnit}
                            </strong>{" "}
                            each ·{" "}
                            <span className="text-muted">{g.milestoneTitle}</span>
                            {" · "}
                            <span className="inv-outward-grouped-table__badge">
                              {g.rows.length} beneficiar{g.rows.length === 1 ? "y" : "ies"}
                            </span>
                          </td>
                        </tr>
                        {g.rows.map((r) => (
                          <tr key={r.id} className="inv-outward-grouped-table__member">
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedRowIds.has(r.id)}
                                onChange={() => toggleOneRow(r.id)}
                                aria-label={`Select ${r.beneficiaryName ?? r.applicationId}`}
                              />
                            </td>
                            <td>
                              <div className="table-name">{r.beneficiaryName ?? "—"}</div>
                            </td>
                            <td className="mono">
                              {r.beneficiaryMobileApplicationId ?? r.applicationId}
                            </td>
                            <td>{r.milestoneTitle}</td>
                            <td>{r.resourceItemLabel}</td>
                            <td className="mono">{r.catalogStandardItemId || "—"}</td>
                            <td>{r.balanceQty}</td>
                            <td>{r.resourceItemUnit}</td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {selectedRows.length === 1 ? (
            <p className="inv-outward-issue__hint">
              Balance for the selected row: <strong>{selectedRows[0]!.balanceQty}</strong> {selectedRows[0]!.resourceItemUnit}
            </p>
          ) : selectedRows.length > 1 ? (
            <p className="inv-outward-issue__hint">
              <strong>{selectedRows.length}</strong> rows selected. In <em>Full balance</em> mode, each row will be
              dispatched at its own balance qty.
            </p>
          ) : null}
          <div className="inv-outward-issue__actions">
            <button
              type="button"
              className="btn btn--primary btn--tg btn--lg"
              disabled={submitting || otpSending || otpModalOpen || selectedRows.length === 0}
              onClick={() => void submitIssue()}
            >
              {otpModalOpen
                ? otpSending
                  ? "Sending OTP…"
                  : submitting
                    ? "Recording dispatch…"
                    : "Complete OTP in dialog"
                : submitting
                  ? "Recording dispatch…"
                  : otpSending
                    ? "Sending OTP…"
                    : `Record outward dispatch${selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}`}
            </button>
          </div>
        </div>
      </div>
      ) : null}

      {otpModalOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!otpConfirming) closeOtpModal();
          }}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="outward-otp-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="outward-otp-title" style={{ marginTop: 0 }}>
              Subcontractor email verification
            </h2>
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              A one-time code is emailed to the subcontractor’s registered contact. Enter that code here to record the
              outward dispatch. The subcontractor must share the code with you.
            </p>
            {otpSending ? <p className="text-muted">Sending email…</p> : null}
            {otpEmailHint ? (
              <p style={{ marginBottom: "0.5rem" }}>
                Code sent to <strong>{otpEmailHint}</strong>
              </p>
            ) : null}
            <label className="field field--full" style={{ display: "block", marginTop: "0.75rem" }}>
              <span className="field__label">6-digit OTP</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="form-control"
                placeholder="000000"
                maxLength={6}
                value={otpInput}
                disabled={otpSending || otpConfirming || !otpChallengeId}
                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </label>
            {otpInlineError ? (
              <p style={{ color: "var(--dash-danger, #b91c1c)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                {otpInlineError}
              </p>
            ) : null}
            <div className="modal__actions" style={{ marginTop: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
              <button
                type="button"
                className="btn"
                disabled={otpConfirming}
                onClick={closeOtpModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--tg-secondary"
                disabled={otpSending || otpConfirming}
                onClick={() => void sendOtpChallenge()}
              >
                {otpChallengeId ? "Resend code" : "Try send again"}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={
                  otpConfirming ||
                  otpSending ||
                  !otpChallengeId ||
                  otpInput.replace(/\D/g, "").length !== 6
                }
                onClick={() => void confirmOtpAndDispatch()}
              >
                {otpConfirming ? "Recording…" : "Verify and dispatch"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {outwardViewMode === "register" ? (
      <div className="card card--gov card--elevated">
        <h3 className="form-section-title" style={{ marginTop: 0 }}>
          Outward register
        </h3>
        <p className="section-lead section-lead--tight text-muted">
          Each issue shows which <strong>subcontractor</strong> received stock and which <strong>beneficiary</strong> it was
          for. Filter by store, firm, or search names / application id.
        </p>

        <div className="goods-receipts-filters" style={{ marginBottom: "1rem" }}>
          <div className="goods-receipts-filters__grid">
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Store / yard</span>
              <select
                className="form-control"
                value={listStoreYardId}
                onChange={(e) => setListStoreYardId(e.target.value)}
              >
                <option value="">All stores / yards</option>
                {sortedYards.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Subcontractor</span>
              <select
                className="form-control"
                value={listSubcontractorId}
                onChange={(e) => setListSubcontractorId(e.target.value)}
              >
                <option value="">All firms</option>
                {sortedMasters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.firmName.trim() || m.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Search</span>
              <input
                type="search"
                className="form-control"
                placeholder="Firm, beneficiary, app id…"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
              />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Sort by time</span>
              <select
                className="form-control"
                value={listSort}
                onChange={(e) => setListSort(e.target.value === "asc" ? "asc" : "desc")}
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
            </label>
          </div>
        </div>

        {registerLoading ? (
          <p className="text-muted">Loading…</p>
        ) : registerError ? (
          <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
            {registerError}
          </p>
        ) : registerRows.length === 0 ? (
          <p className="text-muted">No outward issues recorded yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Store / yard</th>
                  <th>Subcontractor</th>
                  <th>Beneficiary</th>
                  <th>Item</th>
                  <th>Unit</th>
                  <th>Qty</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {registerRows.map((r) => (
                  <tr key={r.id}>
                    <td className="text-muted">{formatOutwardWhen(r.createdAt)}</td>
                    <td>{r.storeYardName?.trim() || r.storeYardId}</td>
                    <td>
                      <span className="table-name">{r.subcontractorFirmName}</span>
                    </td>
                    <td>
                      <div className="table-name">{r.beneficiaryName ?? "—"}</div>
                      <div className="text-muted mono" style={{ fontSize: "0.85em" }}>
                        {r.beneficiaryMobileApplicationId ?? r.applicationId}
                      </div>
                    </td>
                    <td>{r.resourceItemLabel || "—"}</td>
                    <td>{r.resourceItemUnit || "—"}</td>
                    <td>{r.qty}</td>
                    <td className="text-muted">{r.notes?.trim() ? r.notes : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : null}
    </>
  );
}
